import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, Download, Loader2, RefreshCw, Search, X } from 'lucide-react'
import api from '../../utils/api'
import PackageSearchSelect from './PackageSearchSelect'

const EXPORT_SEGMENT_SIZE = 50000

const formatWan = (count) => {
  const wan = count / 10000
  return Number.isInteger(wan) ? String(wan) : String(Math.round(wan * 10) / 10)
}

function formatJsonText(value) {
  if (!value) return '-'

  try {
    return JSON.stringify(JSON.parse(value), null, 2)
  } catch (error) {
    return String(value)
  }
}

export default function UserUninstallLogsTab() {
  const [loading, setLoading] = useState(false)
  const [packagesLoading, setPackagesLoading] = useState(false)
  const [searchInput, setSearchInput] = useState({
    phone: '',
    installerName: '',
    installVersion: '',
    downloadSources: []
  })
  const [filters, setFilters] = useState({
    phone: '',
    installerName: '',
    installVersion: '',
    downloadSources: []
  })
  const [sourceOptions, setSourceOptions] = useState([])
  const [records, setRecords] = useState([])
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 })
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [jumpPage, setJumpPage] = useState('')
  const [exportMenuOpen, setExportMenuOpen] = useState(false)
  const [exporting, setExporting] = useState(false)

  const fetchSourceOptions = async () => {
    setPackagesLoading(true)
    try {
      const res = await api.get('/admin/download-package-names')
      if (res.data.success) {
        const data = res.data.data || res.data || {}
        const labels = Array.from(
          new Set((data.list || []).map((item) => String(item?.label || '').trim()).filter(Boolean))
        )
        setSourceOptions([
          ...labels.map((label, index) => ({
            id: `label-${index}`,
            value: label,
            label
          })),
          {
            id: 'unknown-user',
            value: '__unknown__',
            label: '未知用户'
          },
          {
            id: 'abnormal-client',
            value: '__abnormal__',
            label: '客户端异常登录'
          }
        ])
      }
    } catch (error) {
      console.error('获取下载来源失败:', error)
    } finally {
      setPackagesLoading(false)
    }
  }

  const fetchData = async (page = 1, nextFilters = filters) => {
    const normalizedFilters = {
      phone: String(nextFilters?.phone || '').trim(),
      installerName: String(nextFilters?.installerName || '').trim(),
      installVersion: String(nextFilters?.installVersion || '').trim(),
      downloadSources: Array.isArray(nextFilters?.downloadSources)
        ? nextFilters.downloadSources.map((item) => String(item || '').trim()).filter(Boolean)
        : []
    }

    setLoading(true)
    setExportMenuOpen(false)
    setFilters(normalizedFilters)

    try {
      const response = await api.get('/admin/user-uninstall-logs', {
        params: {
          phone: normalizedFilters.phone,
          installer_name: normalizedFilters.installerName,
          install_version: normalizedFilters.installVersion,
          downloadSources: normalizedFilters.downloadSources.length > 0 ? normalizedFilters.downloadSources : undefined,
          page,
          limit: pagination.limit
        }
      })

      if (response.data.success) {
        const data = response.data.data || response.data || {}
        const pager = data.pagination || {}

        setRecords(data.list || [])
        setPagination({
          page: pager.page || 1,
          limit: pager.limit || pagination.limit,
          total: pager.total || 0,
          totalPages: pager.totalPages || 1
        })
      }
    } catch (error) {
      console.error('获取用户卸载情况失败:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSourceOptions()
    fetchData(1, {
      phone: '',
      installerName: '',
      installVersion: '',
      downloadSources: []
    })
  }, [])

  const handleSearch = (e) => {
    e.preventDefault()
    fetchData(1, searchInput)
  }

  const handleReset = () => {
    const emptyFilters = {
      phone: '',
      installerName: '',
      installVersion: '',
      downloadSources: []
    }
    setSearchInput(emptyFilters)
    setJumpPage('')
    fetchData(1, emptyFilters)
  }

  const handleJump = () => {
    const page = Number(jumpPage)
    if (!jumpPage.trim() || isNaN(page)) {
      setJumpPage('')
      return
    }
    if (page < 1 || page > pagination.totalPages) {
      alert(`请输入 1 ~ ${pagination.totalPages} 之间的页码`)
      return
    }
    fetchData(page, filters)
    setJumpPage('')
  }

  // 导出区间菜单：按当前总数分段，主按钮默认导出最新5万条，菜单项为后续每 5 万一段
  const exportMenuSegments = useMemo(() => {
    const total = pagination.total
    if (!total || total <= EXPORT_SEGMENT_SIZE) return []
    const segCount = Math.ceil(total / EXPORT_SEGMENT_SIZE) - 1
    const segments = []
    for (let i = 1; i <= segCount; i++) {
      const startCount = i * EXPORT_SEGMENT_SIZE
      const endCount = Math.min((i + 1) * EXPORT_SEGMENT_SIZE, total)
      segments.push({ offset: startCount, label: `${formatWan(startCount)}万-${formatWan(endCount)}万条` })
    }
    return segments
  }, [pagination.total])

  const handleExportRange = async (segment) => {
    setExportMenuOpen(false)
    setExporting(true)
    try {
      const response = await api.get('/admin/user-uninstall-logs/export', {
        params: {
          phone: filters.phone || undefined,
          installer_name: filters.installerName || undefined,
          install_version: filters.installVersion || undefined,
          downloadSources: filters.downloadSources.length > 0 ? filters.downloadSources : undefined,
          offset: segment.offset,
          limit: EXPORT_SEGMENT_SIZE
        },
        responseType: 'blob',
        timeout: 0
      })

      const contentType = response.headers?.['content-type'] || ''
      if (contentType.includes('application/json')) {
        const text = await new Response(response.data).text()
        let message = '导出失败'
        try {
          const parsed = JSON.parse(text)
          message = parsed?.error || parsed?.message || message
        } catch (e) { /* ignore */ }
        alert(message)
        return
      }

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `用户卸载情况_${segment.label}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('导出用户卸载情况失败:', error)
      alert('导出失败，请稍后重试')
    } finally {
      setExporting(false)
    }
  }

  const getDownloadSourceText = (item) => {
    if (item?.download_source_label) {
      return item.download_source_label
    }

    if (item?.first_installer_name) {
      return '未知用户'
    }

    return '客户端异常登录'
  }

  const getDownloadSourceFilterText = (values) => {
    if (!Array.isArray(values) || values.length === 0) {
      return '全部'
    }

    return values
      .map((value) => {
        if (value === '__unknown__') {
          return '未知用户'
        }
        if (value === '__abnormal__') {
          return '客户端异常登录'
        }
        return value
      })
      .join('、')
  }

  const selectedRawPostBodyText = useMemo(() => {
    return formatJsonText(selectedRecord?.raw_post_body)
  }, [selectedRecord])

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">用户卸载情况</h2>

        <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput.phone}
              onChange={(e) => setSearchInput((prev) => ({ ...prev, phone: e.target.value }))}
              placeholder="手机号搜索"
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
            />
          </div>
          <input
            type="text"
            value={searchInput.installerName}
            onChange={(e) => setSearchInput((prev) => ({ ...prev, installerName: e.target.value }))}
            placeholder="安装包名模糊搜索"
            className="min-w-[220px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
          />
          <input
            type="text"
            value={searchInput.installVersion}
            onChange={(e) => setSearchInput((prev) => ({ ...prev, installVersion: e.target.value }))}
            placeholder="版本号模糊搜索"
            className="min-w-[180px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
          />
          <PackageSearchSelect
            value={searchInput.downloadSources}
            onChange={(nextValues) => setSearchInput((prev) => ({ ...prev, downloadSources: nextValues }))}
            options={sourceOptions}
            loading={packagesLoading}
            multiple
            placeholder="全部来源"
            className="w-full sm:w-64 min-w-0"
            getOptionValue={(item) => item.value}
            getOptionDisplayName={(item) => item.label}
            searchPlaceholder="搜索渠道来源"
          />
          <button type="submit" className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">
            查询
          </button>
          <button type="button" onClick={handleReset} className="rounded-lg bg-gray-100 px-4 py-2 text-sm text-gray-700 hover:bg-gray-200">
            重置
          </button>
          <button
            type="button"
            onClick={() => fetchData(pagination.page, filters)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            刷新
          </button>
          <div className="relative">
            <div className="flex overflow-hidden rounded-lg border border-emerald-200">
              <button
                type="button"
                onClick={() => handleExportRange({ offset: 0, label: '最新5万条' })}
                disabled={exporting || loading || pagination.total === 0}
                className="inline-flex items-center gap-2 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
              >
                <Download className="h-4 w-4" />
                {exporting ? '导出中…' : '导出最新5万条'}
              </button>
              <button
                type="button"
                onClick={() => setExportMenuOpen((open) => !open)}
                disabled={exporting || loading || exportMenuSegments.length === 0}
                className="border-l border-emerald-200 bg-emerald-50 px-2 py-2 text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                title="选择导出区间"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
            {exportMenuOpen && exportMenuSegments.length > 0 ? (
              <div className="absolute right-0 top-full z-50 mt-1 max-h-72 w-44 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                {exportMenuSegments.map((seg) => (
                  <button
                    key={seg.offset}
                    type="button"
                    onClick={() => handleExportRange(seg)}
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    {seg.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </form>
        {exportMenuOpen ? (
          <div className="fixed inset-0 z-40" onClick={() => setExportMenuOpen(false)} />
        ) : null}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="text-sm text-gray-500">
          当前筛选：
          手机号 {filters.phone || '全部'}，
          安装包 {filters.installerName || '全部'}，
          版本号 {filters.installVersion || '全部'}，
          下载来源 {getDownloadSourceFilterText(filters.downloadSource)}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center rounded-xl border border-gray-200 bg-white py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">用户ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">手机号</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">udid</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">渠道</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">客户端版本</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">install_version</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">installer_name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">下载来源</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">卸载原因</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">卸载时间</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {records.length > 0 ? (
                  records.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{item.user_id || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{item.phone || '-'}</td>
                      <td className="max-w-[240px] truncate px-6 py-4 text-sm text-gray-900" title={item.udid || undefined}>
                        {item.udid || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{item.client_channel || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{item.version || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{item.install_version || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{item.installer_name || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900" title={item.first_installer_name || undefined}>
                        {getDownloadSourceText(item)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {item.uninstall_reasons ? (
                          <div className="flex max-w-[260px] flex-wrap gap-1">
                            {String(item.uninstall_reasons).split(',').filter(Boolean).map((reason, index) => (
                              <span
                                key={`${item.id}-${index}`}
                                className="rounded-full bg-orange-50 px-2 py-0.5 text-xs text-orange-700 dark:bg-orange-900/30 dark:text-orange-300"
                              >
                                {reason}
                              </span>
                            ))}
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{item.uninstall_at || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <button
                          type="button"
                          onClick={() => setSelectedRecord(item)}
                          className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
                        >
                          详情
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={11} className="px-6 py-10 text-center text-sm text-gray-500">
                      暂无卸载记录
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between bg-gray-50 px-6 py-4">
            <div className="text-sm text-gray-600">
              共 {pagination.total} 条，第 {pagination.page}/{pagination.totalPages} 页
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={jumpPage}
                onChange={(e) => setJumpPage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleJump()}
                placeholder="页码"
                min={1}
                max={pagination.totalPages}
                className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <button
                onClick={handleJump}
                className="rounded-lg border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
              >
                跳转
              </button>
              <button
                disabled={pagination.page <= 1}
                onClick={() => fetchData(pagination.page - 1, filters)}
                className="rounded-lg border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                上一页
              </button>
              <button
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => fetchData(pagination.page + 1, filters)}
                className="rounded-lg border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedRecord ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-5xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">卸载详情 #{selectedRecord.id || '-'}</h3>
                <p className="mt-1 text-sm text-gray-500">{selectedRecord.uninstall_at || '-'}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedRecord(null)}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid gap-6 px-6 py-6 md:grid-cols-2">
              <div className="grid grid-cols-2 gap-4 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm">
                <div>
                  <div className="text-gray-500">用户ID</div>
                  <div className="mt-1 font-medium text-gray-900">{selectedRecord.user_id || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">手机号</div>
                  <div className="mt-1 font-medium text-gray-900">{selectedRecord.phone || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">udid</div>
                  <div className="mt-1 break-all font-medium text-gray-900">{selectedRecord.udid || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">用户</div>
                  <div className="mt-1 font-medium text-gray-900">{selectedRecord.nickname || selectedRecord.username || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">安装版本</div>
                  <div className="mt-1 font-medium text-gray-900">{selectedRecord.install_version || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">当前运行版本</div>
                  <div className="mt-1 font-medium text-gray-900">{selectedRecord.version || '-'}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-gray-500">安装包名</div>
                  <div className="mt-1 break-all font-medium text-gray-900">{selectedRecord.installer_name || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">客户端渠道</div>
                  <div className="mt-1 font-medium text-gray-900">{selectedRecord.client_channel || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">客户端时间</div>
                  <div className="mt-1 font-medium text-gray-900">{selectedRecord.client_now_at_raw || '-'}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-gray-500">卸载原因</div>
                  <div className="mt-1 font-medium text-gray-900">
                    {selectedRecord.uninstall_reasons ? (
                      <div className="flex flex-wrap gap-1">
                        {String(selectedRecord.uninstall_reasons).split(',').filter(Boolean).map((reason, index) => (
                          <span
                            key={`reason-${index}`}
                            className="rounded-full bg-orange-50 px-2 py-0.5 text-xs text-orange-700 dark:bg-orange-900/30 dark:text-orange-300"
                          >
                            {reason}
                          </span>
                        ))}
                      </div>
                    ) : (
                      '-'
                    )}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-gray-500">用户建议</div>
                  <div className="mt-1 break-all font-medium text-gray-900">{selectedRecord.suggestion || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">IP</div>
                  <div className="mt-1 font-medium text-gray-900">{selectedRecord.ip_address || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">请求路径</div>
                  <div className="mt-1 font-medium text-gray-900">{selectedRecord.request_path || '-'}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-gray-500">User-Agent</div>
                  <div className="mt-1 break-all font-medium text-gray-900">{selectedRecord.user_agent || '-'}</div>
                </div>
              </div>

              <div>
                <div className="mb-2 text-sm font-medium text-gray-700">raw_post_body</div>
                <div className="max-h-[520px] overflow-auto rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <pre className="whitespace-pre-wrap break-all text-xs leading-6 text-gray-800">{selectedRawPostBodyText}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
